import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  isRtl,
  type AppLocale,
} from '@/i18n';

export type Direction = 'ltr' | 'rtl';

interface LocaleContextValue {
  locale: AppLocale;
  direction: Direction;
  setLocale: (next: AppLocale) => void;
  availableLocales: readonly AppLocale[];
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

function readStoredLocale(): AppLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  return (SUPPORTED_LOCALES as readonly string[]).includes(stored ?? '')
    ? (stored as AppLocale)
    : DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<AppLocale>(() => readStoredLocale());
  const direction: Direction = isRtl(locale) ? 'rtl' : 'ltr';

  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
    const root = document.documentElement;
    root.lang = locale;
    root.dir = direction;
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }, [locale, direction, i18n]);

  const setLocale = useCallback((next: AppLocale) => {
    if (!SUPPORTED_LOCALES.includes(next)) return;
    setLocaleState(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      direction,
      setLocale,
      availableLocales: SUPPORTED_LOCALES,
    }),
    [locale, direction, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return ctx;
}
